import { ActionIcon, Divider, Group, Loader, Sx, Tooltip } from '@mantine/core';
import { IconEyeCheck, IconRefresh, IconSend, IconTemplate } from '@tabler/icons';
import { FC, useContext } from 'react';
import { EmailBoxContext } from '../../Service/Context/EmailBoxContext';
import { MessageContext } from '../../Service/Context/MessageContextV2';
import { useEventScope } from '../../Service/hooks/useEventScope';

interface Props {
  loadTemplate: () => void;
}

export const folderActionIconSx: Sx = (theme) => {
  const isDark = theme.colorScheme === 'dark';

  return {
    borderColor: 'transparent',
    color: isDark ? theme.colors.dark[0] : theme.colors.gray[7],
    transition: 'background-color 120ms ease, color 120ms ease',

    '&:not(:disabled):hover': {
      backgroundColor: isDark ? theme.fn.rgba(theme.colors.blue[1], 0.16) : theme.colors.gray[1],
      color: isDark ? theme.colors.blue[1] : theme.colors.blue[4],
    },

    '&:not(:disabled):hover svg': {
      color: isDark ? theme.colors.blue[1] : theme.colors.blue[4],
      stroke: isDark ? theme.colors.blue[1] : theme.colors.blue[4],
    },

    '&&:disabled, &&[data-disabled]': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      boxShadow: 'none',
      color: isDark ? theme.fn.rgba(theme.colors.dark[0], 0.38) : theme.colors.gray[4],
      cursor: 'not-allowed',
      opacity: 1,
    },
  };
};

const EmailFolderActions: FC<Props> = ({ loadTemplate }) => {
  const { isEventScope } = useEventScope();
  const { action, loading: loadingMessage, initMessage } = useContext(MessageContext);
  const { refresh, loading, messages, changingSeenFlag, markMessagesInFolderAsRead } = useContext(EmailBoxContext);

  const hasUnreadMessages = messages.some(({ imap, localDateTime, readTimestamp, sendByUser }) =>
    imap ? !readTimestamp : localDateTime && !readTimestamp && !sendByUser,
  );
  const showMarkMessagesAsRead = hasUnreadMessages && !loading && !changingSeenFlag;
  const showTemplateAction = isEventScope;
  const showNewEmailAction = action !== 'NEW';
  const showActionsAfterRefresh = showMarkMessagesAsRead || showTemplateAction || showNewEmailAction;

  return (
    <Group
      py={5}
      px="sm"
      spacing="xs"
      sx={(theme) => ({
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        borderRadius: 25,
      })}
    >
      <>
        <Tooltip openDelay={300} withArrow transition="slide-down" label="Aktualisierung">
          <ActionIcon disabled={loading} size="sm" sx={folderActionIconSx} onClick={refresh}>
            <IconRefresh />
          </ActionIcon>
        </Tooltip>

        {showActionsAfterRefresh && <Divider orientation="vertical" />}
        {showMarkMessagesAsRead && (
          <Tooltip openDelay={300} withArrow transition="slide-down" label="Alle als gelesen markieren">
            <ActionIcon size="sm" sx={folderActionIconSx} onClick={markMessagesInFolderAsRead}>
              <IconEyeCheck />
            </ActionIcon>
          </Tooltip>
        )}

        {showMarkMessagesAsRead && (showTemplateAction || showNewEmailAction) && <Divider orientation="vertical" />}
        {showTemplateAction && (
          <>
            <Tooltip openDelay={300} withArrow transition="slide-down" label="Vorlage laden">
              <ActionIcon size="sm" sx={folderActionIconSx} onClick={loadTemplate}>
                <IconTemplate />
              </ActionIcon>
            </Tooltip>
            {showNewEmailAction && <Divider orientation="vertical" />}
          </>
        )}
        {showNewEmailAction && (
          <>
            {loadingMessage && <Loader size="xs" />}
            {!loadingMessage && (
              <Tooltip openDelay={300} withArrow transition="slide-down" label="Neue E-Mail schreiben">
                <ActionIcon size="sm" sx={folderActionIconSx} onClick={() => initMessage()}>
                  <IconSend />
                </ActionIcon>
              </Tooltip>
            )}
          </>
        )}
      </>
    </Group>
  );
};

export default EmailFolderActions;
